import React, { useEffect, useMemo, useState } from 'react';
import { CustomLayerProps, ResponsiveLine, Serie } from '@nivo/line';
import { linearGradientDef } from '@nivo/core';
import { useTheme } from 'styled-components';
import { useMediaQuery } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { formatDecimal } from '@app/common';
import { cn } from '../../../../../utils/tailwind';
import { Tooltip } from '../../../../../components/app/tooltip/tooltip';
import { CompoundInterestYearResult, CompoundInterestParams } from '../../../../../utils/compound-interest/types';
import { MARKET_SCENARIOS } from '../../../../../utils/compound-interest/constants';
import { calculateScenarioResults } from '../../../../../utils/compound-interest/calculations';

interface CompoundInterestGrowthChartProps {
    results: CompoundInterestYearResult[];
    params: CompoundInterestParams;
}

type SeriesKey = 'totalValue' | 'deposited' | 'netValue' | 'realValue';
type ScenarioSeriesKey = 'optimistic' | 'neutral' | 'pessimistic' | 'custom';

const GrowthChartEndpoints = ({ series }: CustomLayerProps) => (
    <g pointerEvents="none" aria-hidden="true">
        {series.map((serie) => {
            const endpoint = serie.data[serie.data.length - 1];
            if (!endpoint) return null;

            return (
                <circle
                    key={serie.id}
                    cx={endpoint.position.x}
                    cy={endpoint.position.y}
                    r={4}
                    fill={serie.color}
                    stroke="#fdf7f2"
                    strokeWidth={2}
                />
            );
        })}
    </g>
);

export const CompoundInterestGrowthChart: React.FC<CompoundInterestGrowthChartProps> = ({ results, params }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const baseYear = new Date().getFullYear();
    const isMobile = useMediaQuery('(max-width: 599px)');

    const [visibleSeries, setVisibleSeries] = useState<Record<SeriesKey, boolean>>({
        totalValue: true,
        deposited: true,
        netValue: false,
        realValue: false,
    });

    const [visibleScenarioSeries, setVisibleScenarioSeries] = useState<Record<ScenarioSeriesKey, boolean>>({
        optimistic: false,
        neutral: false,
        pessimistic: false,
        custom: false,
    });

    const isNetValueSeriesDisabled = params.taxRate === 0;

    useEffect(() => {
        if (!isNetValueSeriesDisabled) {
            return;
        }

        setVisibleSeries((prev) => {
            if (!prev.netValue) {
                return prev;
            }

            return {
                ...prev,
                netValue: false,
            };
        });
    }, [isNetValueSeriesDisabled]);

    const seriesConfig: Array<{ key: SeriesKey; color: string; label: string }> = useMemo(
        () => [
            {
                key: 'totalValue',
                color: theme.palette.color.primary.light,
                label: t('pages.compoundInterest.charts.growth.totalValue'),
            },
            {
                key: 'deposited',
                color: theme.palette.color.secondary.main,
                label: t('pages.compoundInterest.charts.growth.deposited'),
            },
            {
                key: 'netValue',
                color: theme.palette.color.warning.main,
                label: t('pages.compoundInterest.charts.growth.netValue'),
            },
            {
                key: 'realValue',
                color: theme.palette.color.blue.main,
                label: t('pages.compoundInterest.charts.growth.realValue'),
            },
        ],
        [theme, t],
    );

    const scenarioSeriesConfig: Array<{ key: ScenarioSeriesKey; color: string; label: string }> = useMemo(
        () => [
            {
                key: 'optimistic',
                color: theme.palette.color.positive.main,
                label: t('pages.compoundInterest.scenarios.optimisticShort'),
            },
            {
                key: 'neutral',
                color: theme.palette.color.gray.main,
                label: t('pages.compoundInterest.scenarios.neutralShort'),
            },
            {
                key: 'pessimistic',
                color: theme.palette.color.negative.main,
                label: t('pages.compoundInterest.scenarios.pessimisticShort'),
            },
            {
                key: 'custom',
                color: theme.palette.color.purple.main,
                label: t('pages.compoundInterest.scenarios.custom'),
            },
        ],
        [theme, t],
    );

    const activeScenarioKey = useMemo<ScenarioSeriesKey>(() => {
        const matchingScenario = MARKET_SCENARIOS.find(
            (scenario) =>
                scenario.interestRate === params.interestRate && scenario.inflationRate === params.inflationRate,
        );

        return (matchingScenario?.id as ScenarioSeriesKey) || 'custom';
    }, [params.inflationRate, params.interestRate]);

    const scenarioResults = useMemo(() => {
        return calculateScenarioResults(params, [
            ...MARKET_SCENARIOS,
            {
                id: 'custom',
                interestRate: params.interestRate,
                inflationRate: params.inflationRate,
                label: 'pages.compoundInterest.scenarios.custom',
            },
        ]);
    }, [params]);

    const chartData: Serie[] = useMemo(() => {
        const series: Serie[] = [];

        if (visibleSeries.totalValue) {
            series.push({
                id: 'totalValue',
                data: results.map((result) => ({
                    x: baseYear + result.year,
                    y: result.totalValue,
                })),
            });
        }

        if (visibleSeries.deposited) {
            series.push({
                id: 'deposited',
                data: results.map((result) => ({
                    x: baseYear + result.year,
                    y: result.totalDeposited,
                })),
            });
        }

        if (visibleSeries.netValue && !isNetValueSeriesDisabled) {
            series.push({
                id: 'netValue',
                data: results.map((result) => ({
                    x: baseYear + result.year,
                    y: result.netValue,
                })),
            });
        }

        if (visibleSeries.realValue) {
            series.push({
                id: 'realValue',
                data: results.map((result) => ({
                    x: baseYear + result.year,
                    y: result.realValue,
                })),
            });
        }

        (Object.keys(visibleScenarioSeries) as ScenarioSeriesKey[]).forEach((scenarioKey) => {
            if (!visibleScenarioSeries[scenarioKey] || scenarioKey === activeScenarioKey) {
                return;
            }

            const scenarioResult = scenarioResults.get(scenarioKey) ?? [];
            series.push({
                id: `scenario:${scenarioKey}`,
                data: scenarioResult.map((result) => ({
                    x: baseYear + result.year,
                    y: result.totalValue,
                })),
            });
        });

        return series;
    }, [
        activeScenarioKey,
        baseYear,
        isNetValueSeriesDisabled,
        results,
        scenarioResults,
        visibleScenarioSeries,
        visibleSeries,
    ]);

    const getColor = (serie: Serie): string => {
        if (String(serie.id).startsWith('scenario:')) {
            const scenarioKey = String(serie.id).replace('scenario:', '') as ScenarioSeriesKey;
            const scenarioConfig = scenarioSeriesConfig.find((config) => config.key === scenarioKey);
            return scenarioConfig?.color || theme.palette.color.gray.main;
        }

        const config = seriesConfig.find((item) => item.key === serie.id);
        return config?.color || theme.palette.color.gray.main;
    };

    const toggleSeries = (key: SeriesKey) => {
        if (key === 'netValue' && isNetValueSeriesDisabled) {
            return;
        }

        setVisibleSeries((prev) => {
            const isCurrentlyVisible = prev[key];

            if (isCurrentlyVisible) {
                const activeBaseSeries = Object.values(prev).filter(Boolean).length;
                const activeScenarioSeries = Object.values(visibleScenarioSeries).filter(Boolean).length;

                if (activeBaseSeries + activeScenarioSeries <= 1) {
                    return prev;
                }
            }

            return {
                ...prev,
                [key]: !prev[key],
            };
        });
    };

    const toggleScenarioSeries = (key: ScenarioSeriesKey) => {
        setVisibleScenarioSeries((prev) => {
            const isCurrentlyVisible = prev[key];

            if (isCurrentlyVisible) {
                const activeScenarioSeries = Object.values(prev).filter(Boolean).length;
                const activeBaseSeries = Object.values(visibleSeries).filter(Boolean).length;

                if (activeBaseSeries + activeScenarioSeries <= 1) {
                    return prev;
                }
            }

            return {
                ...prev,
                [key]: !prev[key],
            };
        });
    };

    const leftMargin = useMemo(() => {
        const allYValues = chartData.flatMap((serie) => serie.data.map((point) => Number(point.y) || 0));
        const maxValue = Math.max(...allYValues, 0);
        const formattedMax = isMobile
            ? new Intl.NumberFormat('cs-CZ', {
                  notation: 'compact',
                  compactDisplay: 'short',
                  maximumFractionDigits: 1,
              }).format(maxValue)
            : formatDecimal(maxValue, 0);
        const calculated = Math.max(56, formattedMax.length * 8 + 14);

        return isMobile ? Math.min(64, calculated) : Math.max(80, calculated);
    }, [chartData, isMobile]);

    const visibleYears = useMemo(() => {
        const totalYears = results.length;
        let step: number;

        if (isMobile) {
            if (totalYears <= 10) step = 2;
            else if (totalYears <= 20) step = 4;
            else if (totalYears <= 30) step = 6;
            else step = 10;
        } else if (totalYears <= 10) step = 2;
        else if (totalYears <= 20) step = 4;
        else if (totalYears <= 30) step = 10;
        else step = 20;

        const set = new Set(results.filter((_, index) => index % step === 0).map((result) => baseYear + result.year));

        if (!isMobile) {
            set.add(baseYear + results[totalYears - 1].year);
        }

        return set;
    }, [baseYear, isMobile, results]);

    const formatAxisValue = (value: number) => {
        if (!isMobile) {
            return formatDecimal(value, 0);
        }

        return new Intl.NumberFormat('cs-CZ', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: 1,
        }).format(value);
    };

    const chartContent = (
        <>
            <div className="h-[400px] w-full [&_svg]:overflow-visible md:h-[450px] max-md:mx-[-1.2rem] max-md:w-[calc(100%+2.4rem)]">
                <ResponsiveLine
                    data={chartData}
                    margin={{ top: 20, right: isMobile ? 28 : 0, bottom: isMobile ? 60 : 50, left: leftMargin }}
                    xScale={{ type: 'point' }}
                    yScale={{
                        type: 'linear',
                        min: 0,
                        max: 'auto',
                        stacked: false,
                    }}
                    curve="monotoneX"
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: isMobile ? 10 : 16,
                        tickRotation: isMobile ? 30 : 0,
                        format: (value) => (visibleYears.has(Number(value)) ? String(value) : ''),
                        renderTick: isMobile
                            ? undefined
                            : ({ value, x, y, textX, textY }) => (
                                  <g transform={`translate(${x},${y})`}>
                                      <text
                                          x={textX}
                                          y={textY}
                                          textAnchor={
                                              Number(value) === baseYear + results[results.length - 1].year
                                                  ? 'end'
                                                  : 'middle'
                                          }
                                          dominantBaseline="hanging"
                                          fill="#72645a"
                                          fontSize={14}
                                      >
                                          {visibleYears.has(Number(value)) ? String(value) : ''}
                                      </text>
                                  </g>
                              ),
                    }}
                    axisLeft={{
                        tickSize: 0,
                        tickPadding: isMobile ? 8 : 16,
                        tickRotation: 0,
                        format: (value) => formatAxisValue(value as number),
                        tickValues: 5,
                    }}
                    colors={(serie) => getColor(serie)}
                    enablePoints={false}
                    enableArea={true}
                    areaOpacity={0.05}
                    layers={[
                        'grid',
                        'markers',
                        'axes',
                        'areas',
                        'crosshair',
                        'lines',
                        GrowthChartEndpoints,
                        'slices',
                        'mesh',
                        'legends',
                    ]}
                    enableGridX={false}
                    enableGridY={false}
                    defs={[
                        linearGradientDef('gradient', [
                            { offset: 0, color: 'inherit', opacity: 0.3 },
                            { offset: 100, color: 'inherit', opacity: 0 },
                        ]),
                    ]}
                    fill={[{ match: '*', id: 'gradient' }]}
                    enableSlices={isMobile ? false : 'x'}
                    sliceTooltip={({ slice }) => (
                        <div
                            style={{
                                background: theme.palette.background.body,
                                padding: '10px 12px',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                border: `1px solid ${theme.palette.common.border}`,
                                maxWidth: isMobile ? '180px' : '260px',
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: '8px', color: theme.palette.common.text }}>
                                {t('pages.compoundInterest.table.year')} {slice.points[0].data.xFormatted}
                            </div>
                            {slice.points.map((point) => {
                                const isScenarioPoint = String(point.serieId).startsWith('scenario:');
                                const scenarioKey = String(point.serieId).replace('scenario:', '') as ScenarioSeriesKey;
                                const config = isScenarioPoint
                                    ? scenarioSeriesConfig.find((item) => item.key === scenarioKey)
                                    : seriesConfig.find((item) => item.key === point.serieId);

                                return (
                                    <div
                                        key={point.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '4px',
                                            color: theme.palette.common.text,
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                background: point.serieColor,
                                            }}
                                        />
                                        <span style={{ fontSize: '13px' }}>
                                            {config?.label || point.serieId}: {formatDecimal(point.data.y as number)}{' '}
                                            {params.currency}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    theme={{
                        axis: {
                            ticks: {
                                text: {
                                    fill: '#72645a',
                                    fontSize: isMobile ? 12 : 14,
                                },
                            },
                        },
                        grid: {
                            line: {
                                stroke: theme.palette.common.border,
                                strokeWidth: 1,
                            },
                        },
                        crosshair: {
                            line: {
                                stroke: theme.palette.common.textMuted,
                                strokeWidth: 1,
                                strokeOpacity: 0.5,
                            },
                        },
                    }}
                />
            </div>

            <div className="mt-[1.5rem] flex flex-wrap justify-center gap-[1rem]">
                {seriesConfig.map((config) => {
                    const isSeriesDisabled = config.key === 'netValue' && isNetValueSeriesDisabled;
                    const isSeriesActive = !isSeriesDisabled && visibleSeries[config.key];

                    let legendClassName = 'opacity-50 hover:opacity-100';
                    if (isSeriesDisabled) {
                        legendClassName = 'cursor-not-allowed opacity-[0.35] hover:opacity-[0.35]';
                    } else if (isSeriesActive) {
                        legendClassName = 'opacity-100';
                    }

                    const legendBorderColor = isSeriesActive ? config.color : theme.palette.common.border;
                    const legendBackground = isSeriesActive ? `${config.color}15` : 'transparent';

                    const legendItem = (
                        <button
                            aria-disabled={isSeriesDisabled}
                            onClick={() => toggleSeries(config.key)}
                            type="button"
                            className={cn(
                                'flex items-center gap-[0.5rem] rounded-[99rem] border px-[1.2rem] py-[0.6rem] transition-all',
                                legendClassName,
                            )}
                            style={{
                                borderColor: legendBorderColor,
                                background: legendBackground,
                            }}
                        >
                            <span className="h-[10px] w-[10px] rounded-[50%]" style={{ background: config.color }} />
                            <span className="text-small text-text">{config.label}</span>
                        </button>
                    );

                    if (!isSeriesDisabled) {
                        return <React.Fragment key={config.key}>{legendItem}</React.Fragment>;
                    }

                    return (
                        <Tooltip
                            key={config.key}
                            content={t('pages.compoundInterest.calculator.zeroTaxRateTooltip')}
                            position="top"
                        >
                            {legendItem}
                        </Tooltip>
                    );
                })}

                {scenarioSeriesConfig
                    .filter((config) => config.key !== activeScenarioKey)
                    .map((config) => (
                        <button
                            key={`scenario-toggle-${config.key}`}
                            onClick={() => toggleScenarioSeries(config.key)}
                            type="button"
                            className={cn(
                                'flex items-center gap-[0.5rem] rounded-[99rem] border px-[1.2rem] py-[0.6rem] opacity-50 transition-all hover:opacity-100',
                                visibleScenarioSeries[config.key] && 'opacity-100',
                            )}
                            style={{
                                borderColor: visibleScenarioSeries[config.key]
                                    ? config.color
                                    : theme.palette.common.border,
                                background: visibleScenarioSeries[config.key] ? `${config.color}15` : 'transparent',
                            }}
                        >
                            <span className="h-[10px] w-[10px] rounded-[50%]" style={{ background: config.color }} />
                            <span className="text-small text-text">{config.label}</span>
                        </button>
                    ))}
            </div>
        </>
    );

    return (
        <section aria-label={t('pages.compoundInterest.charts.growth.title')}>
            <h2 className="mb-[2.4rem] text-[clamp(2.8rem,4vw,4.4rem)] leading-[1.15] font-semibold tracking-[-0.035em] text-brand-secondary md:mb-[3.2rem]">
                {t('pages.compoundInterest.charts.growth.title')}
            </h2>
            {chartContent}
        </section>
    );
};
